import React, { ReactElement } from 'react'
import Select, { type OptionProps, MultiValue, SingleValue, ValueContainerProps, components } from 'react-select'

import './select.css'

export type OptionType = {
  label: string
  value: string
  tag?: React.ReactNode
}
type BaseProps = {
  options: Array<OptionType>
  label?: string
  name: string
  isDisabled?: boolean
  required?: boolean
  autoMinWidth?: boolean
  alwaysVisibleLabels?: string[]
  hideSelectedOptions?: boolean
  isSearchable?: boolean
  size?: 'sm' | 'lg'
  showBorder?: boolean
}

type SingleSelectProps = BaseProps & {
  value: OptionType | undefined
  onChange: (value: string) => void
  isMulti: false
}

type MultiSelectProps = BaseProps & {
  isMulti?: true
  value: MultiValue<OptionType>
  onChange: (value: string[]) => void
}

const SelectInput: React.FC<SingleSelectProps | MultiSelectProps> = ({ options, label, onChange, name, value, isDisabled, isMulti = true, required, autoMinWidth, alwaysVisibleLabels, hideSelectedOptions, isSearchable = true, size = 'sm', showBorder }) => {
  const handleChange = (selected: MultiValue<OptionType> | SingleValue<OptionType>) => {
    if (isMulti) {
      (onChange as (value: string[]) => void)((selected as MultiValue<OptionType>).map(({ value }) => value))
    } else {
      (onChange as (value: string) => void)((selected as OptionType)?.value || '')
    }
  }

  const customFilterOption = (option: OptionType, inputValue: string) => {
    // alwaysVisibleLabels are always visible but will respect the sorting of the options
    if (alwaysVisibleLabels?.includes(option.label)) {
      return true
    }

    // Default case-insensitive filtering for other options
    return option.label.toLowerCase().includes(inputValue.toLowerCase())
  }

  return (
    <Select
      name={name}
      value={value ?? []}
      onChange={handleChange}
      options={options}
      components={{
        ValueContainer: (props) => <ValueContainer {...props} size={size} label={label} autoMinWidth={autoMinWidth} />,
        Option: (props: OptionProps<OptionType, boolean>) => (
          <components.Option {...props}>
            <div className='flex gap-x-1 justify-between'>
              {props.data.label}
              {props.data.tag}
            </div>
          </components.Option>
        ),
      }}
      styles={{
        control: (baseStyles) => ({
          ...baseStyles,
          border: showBorder ? 'solid 1px border-color': 'none',
        }),
      }}
      className={`${isDisabled ? 'opacity-70' : ''}`}
      classNamePrefix='zriveSelect'
      isMulti={isMulti}
      hideSelectedOptions={hideSelectedOptions}
      placeholder={label}
      isDisabled={isDisabled}
      required={required}
      menuPlacement='auto'
      filterOption={customFilterOption}
      isSearchable={isSearchable}
    />
  )
}

interface MyValueContainerProps<T> extends ValueContainerProps<T> {
  label?: string
  autoMinWidth?: boolean
  size: 'sm' | 'lg'
}
const ValueContainer = ({ children, label, autoMinWidth, size, ...props }: MyValueContainerProps<OptionType>): ReactElement => {
  const [values] = React.Children.toArray(children) as Array<ReactElement>
  const valueLength = props?.getValue()?.length
  const hasValuesSelected = valueLength > 0
  const selectedLabelClass = hasValuesSelected
    ? `${label ? 'pt-4' : ''} ${size === 'sm' ? 'text-2xs sm:text-xs text-black' : 'font-astrid leading-none text-3xl text-dark-green' } text-ellipsis overflow-hidden max-w-full`
    : 'text-2xs sm:text-sm pl-1 pt-3 sm:pt-2 text-gray'

  const searchValue = (children as ReactElement[])[1]?.props?.value
  return (
    <div className={`h-[42px] relative ${hasValuesSelected ? 'contents' : ''} ${autoMinWidth ? '' : 'min-w-[200px]'}`}>
      {/*  children[1] renders search text box */}
      {<div className='translate-x-2 top-4 absolute h-2'>{(children as ReactElement[])[1]}</div>}
      <components.ValueContainer {...props}>
          {valueLength > 0 && label && <span className={`${searchValue.length > 1 ? '-top-1' : 'top-0'} absolute px-1 text-3xs text-gray`}>{label}</span>}
          {valueLength > 1 ? (
            <span className={selectedLabelClass}>{`${props.selectProps.placeholder} (${valueLength})`}</span>
          ) : (
            <span className={`flex gap-x-2 items-center ${selectedLabelClass}`}>{values.props.children}{values.props.data?.tag}</span>
          )}
      </components.ValueContainer>
    </div>
  )
}

export default SelectInput

